import React, { useEffect, useState } from "react";
import { Typography, Button, TextField } from "@material-ui/core";
import styled, { css } from "styled-components";
import { useTheme } from "@material-ui/styles";
import { Formik, FormikProps } from "formik";
import * as yup from "yup";
import { Theme } from "../../../theme";

const StyledSection = styled.section`
  ${({ theme }: { theme: Theme }) => {
    return css`
      width: 700px;
    
      .form{
          padding-left:100px;
          padding-right:100px;
          padding-bottom:125px;
          flex-basis: 700px;
          ${theme.breakpoints.down("sm")}{
              padding-left: ${theme.spacing(2)}px
              padding-right: ${theme.spacing(2)}px
          }
      }
      .title{
          margin-top: ${theme.spacing(3)}px
      }
      .textField{
          margin-top: ${theme.spacing(1.5)}px
      }
      .ConfirmResetPasswordButton{
          margin: ${theme.spacing(2, 0)}
      }
    `;
  }};
`;

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string()
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!$%@#£€*?&]{8,}$/,
      "Password must contain atleast 8 characters, one capital letter and one number"
    )
    .required("Enter your password"),
  code: yup
    .string()
    .min(6, "Code must be at least 6 characters long")
    .required("Code is required"),
});

interface Values {
  email: string;
  password: string;
  code: string;
}

export interface ForgotPasswordProps {
  sharedEmail: string;
  goToSignup: () => void;
  goToSignin: () => void;
  handleForgotPassword: (email: string) => void;
  handleForgotPasswordConfirm: (
    email: string,
    password: string,
    code: string
  ) => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = props => {
  const theme: Theme = useTheme();
  const [timer, setTimer] = useState<any>(undefined);

  useEffect(() => {
    //On first render send them a code to their email
    // props.handleForgotPassword(props.sharedEmail); //DISABLED FOR NOW

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <StyledSection theme={theme}>
        <div className="form">
          <Formik
            initialValues={{ email: props.sharedEmail, password: "", code: "" }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              await props.handleForgotPasswordConfirm(
                values.email,
                values.password,
                values.code
              );
              const timeout = setTimeout(() => {
                setSubmitting(false);
              }, 500);
              setTimer(timeout);
            }}
          >
            {({
              values,
              touched,
              errors,
              dirty,
              isSubmitting,
              handleChange,
              handleBlur,
              handleSubmit,
              handleReset,
              submitForm,
            }: FormikProps<Values>) => (
              <>
                <Typography className="title" variant="h2">
                  Reset Password
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                  A verification code was sent to your email.
                </Typography>

                <TextField
                  autoFocus
                  autoComplete="email"
                  className="textField"
                  error={!!(errors.email && touched.email)}
                  fullWidth
                  helperText={errors.email && touched.email && errors.email}
                  label="Email address"
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="text"
                  value={values.email}
                  variant="outlined"
                />
                <TextField
                  className="textField"
                  error={!!(errors.password && touched.password)}
                  fullWidth
                  helperText={
                    errors.password && touched.password && errors.password
                  }
                  label="Password"
                  name="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="password"
                  value={values.password}
                  variant="outlined"
                />
                <TextField
                  className="textField"
                  error={!!(errors.code && touched.code)}
                  fullWidth
                  helperText={errors.code && touched.code && errors.code}
                  label="Code"
                  name="code"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="text"
                  value={values.code}
                  variant="outlined"
                />
                <Button
                  className="ConfirmResetPasswordButton"
                  type="submit"
                  disabled={
                    isSubmitting || !dirty || Object.keys(errors).length > 0
                  }
                  onClick={submitForm}
                  color="primary"
                  fullWidth
                  size="large"
                  variant="contained"
                >
                  Reset your password
                </Button>
                <Typography
                  color="textSecondary"
                  variant="body1"
                  onClick={e => {
                    e.preventDefault();
                    props.handleForgotPassword(props.sharedEmail);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  Resend verification code
                </Typography>
                <Typography
                  color="textSecondary"
                  variant="body1"
                  onClick={props.goToSignup}
                  style={{ cursor: "pointer" }}
                >
                  Don't have an account?
                </Typography>
                <Typography
                  color="textSecondary"
                  variant="body1"
                  onClick={props.goToSignin}
                  style={{ cursor: "pointer" }}
                >
                  Go back to sign in
                </Typography>
              </>
            )}
          </Formik>
        </div>
      </StyledSection>
    </>
  );
};

export default ForgotPassword;
